<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import aiIgnoredNumbersAPI from 'dashboard/api/aiIgnoredNumbers';

const { t } = useI18n();

const records = ref([]);
const loading = ref(true);
const saving = ref(false);
const form = ref({ phone_number: '', name: '' });

const tk = key => t(`AI_IGNORED_NUMBERS.${key}`);

const fetchRecords = async () => {
  loading.value = true;
  try {
    const { data } = await aiIgnoredNumbersAPI.get();
    records.value = data;
  } catch {
    records.value = [];
  } finally {
    loading.value = false;
  }
};

const add = async () => {
  if (!form.value.phone_number.trim()) return;
  saving.value = true;
  try {
    const { data } = await aiIgnoredNumbersAPI.create({
      ai_ignored_number: {
        phone_number: form.value.phone_number,
        name: form.value.name,
      },
    });
    records.value = [data, ...records.value];
    form.value = { phone_number: '', name: '' };
    useAlert(tk('ADD_SUCCESS'));
  } catch (error) {
    useAlert(error?.response?.data?.error || tk('ADD_ERROR'));
  } finally {
    saving.value = false;
  }
};

const remove = async id => {
  try {
    await aiIgnoredNumbersAPI.delete(id);
    records.value = records.value.filter(r => r.id !== id);
    useAlert(tk('DELETE_SUCCESS'));
  } catch {
    useAlert(tk('DELETE_ERROR'));
  }
};

onMounted(fetchRecords);
</script>

<template>
  <div class="max-w-2xl p-6">
    <div class="mb-5">
      <h2 class="text-lg font-semibold text-n-slate-12">{{ tk('TITLE') }}</h2>
      <p class="text-sm text-n-slate-11">{{ tk('DESCRIPTION') }}</p>
    </div>

    <!-- Adicionar -->
    <div
      class="flex flex-col gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-4 sm:flex-row sm:items-end"
    >
      <label class="flex flex-1 flex-col gap-1">
        <span class="text-sm font-medium text-n-slate-11">{{
          tk('PHONE_LABEL')
        }}</span>
        <input
          v-model="form.phone_number"
          type="text"
          :placeholder="tk('PHONE_PH')"
          class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
          @keyup.enter="add"
        />
      </label>
      <label class="flex flex-1 flex-col gap-1">
        <span class="text-sm font-medium text-n-slate-11">{{
          tk('NAME_LABEL')
        }}</span>
        <input
          v-model="form.name"
          type="text"
          :placeholder="tk('NAME_PH')"
          class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
          @keyup.enter="add"
        />
      </label>
      <button
        type="button"
        class="rounded-lg bg-n-brand px-5 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-40"
        :disabled="saving"
        @click="add"
      >
        {{ saving ? tk('ADDING') : tk('ADD') }}
      </button>
    </div>

    <!-- Lista -->
    <div class="mt-5">
      <div v-if="loading" class="text-sm text-n-slate-10">
        {{ tk('LOADING') }}
      </div>
      <div
        v-else-if="records.length === 0"
        class="rounded-xl border border-dashed border-n-weak p-8 text-center text-sm text-n-slate-10"
      >
        {{ tk('EMPTY') }}
      </div>
      <ul v-else class="flex flex-col divide-y divide-n-weak">
        <li
          v-for="record in records"
          :key="record.id"
          class="flex items-center justify-between py-3"
        >
          <div class="flex flex-col">
            <span class="text-sm font-medium text-n-slate-12">
              {{ record.name || tk('NO_NAME') }}
            </span>
            <span class="text-sm text-n-slate-11">{{
              record.phone_number
            }}</span>
          </div>
          <button
            type="button"
            class="flex items-center gap-1 rounded-lg border border-n-weak px-3 py-1.5 text-sm text-n-ruby-11 hover:bg-n-alpha-2"
            @click="remove(record.id)"
          >
            <span class="i-lucide-trash-2 size-4" />
            {{ tk('REMOVE') }}
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>
